import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import { emptyOfferDetails, offerDetailsValid } from '../constants/offer';
import OfferDetailsForm from './OfferDetailsForm';

export default function OfferStaffReply({ app, onUpdated }) {
  const qc = useQueryClient();
  const [message, setMessage] = useState('');
  const [resolution, setResolution] = useState('continue');
  const [offerDetails, setOfferDetails] = useState(
    emptyOfferDetails(app.offerDetails || {})
  );
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: payload =>
      api.post(`/applications/${app.id}/offer-reply`, payload).then(r => r.data),
    onSuccess: data => {
      setMessage('');
      setError('');
      qc.invalidateQueries({ queryKey: ['applications'] });
      qc.invalidateQueries({ queryKey: ['myApplications'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
      onUpdated?.(data);
    },
    onError: err => setError(err.response?.data?.message || 'Failed to send reply')
  });

  if (app.stage !== 'offer' || app.offerStatus === 'declined') return null;

  const showRevisedTerms = resolution === 'revise_offer';
  const canSend =
    message.trim() &&
    (!showRevisedTerms || offerDetailsValid(offerDetails));

  return (
    <div className="border border-[hsl(var(--ds-primary)/0.2)] bg-[hsl(var(--ds-primary)/0.06)]/30 p-3 space-y-2">
      <p className="text-sm font-medium text-[hsl(var(--ds-fg))]">Reply on offer / negotiation</p>
      {app.candidateNegotiation && (
        <div className="text-xs bg-amber-50 border border-amber-200 p-2 space-y-1">
          <p className="font-medium text-amber-900">Candidate counter proposal</p>
          {app.candidateNegotiation.expectedPackage && (
            <p>Package: {app.candidateNegotiation.expectedPackage}</p>
          )}
          {app.candidateNegotiation.expectedJoiningDate && (
            <p>Joining: {app.candidateNegotiation.expectedJoiningDate}</p>
          )}
          {app.candidateNegotiation.comments && <p>{app.candidateNegotiation.comments}</p>}
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="field-with-label">
        <textarea
          className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
          rows={3}
          placeholder=" "
          value={message}
          onChange={e => setMessage(e.target.value)}
        />
        <label>Message to candidate</label>
      </div>
      <div className="field-with-label">
        <select
          className="h-9 w-full rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-3 text-sm text-[hsl(var(--ds-fg))] outline-none hover:border-[hsl(var(--ds-primary)/0.45)] focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]"
          value={resolution}
          onChange={e => setResolution(e.target.value)}
        >
          <option value="continue">Continue negotiation (no status change)</option>
          <option value="accept_terms">Accept negotiated terms (mark offer accepted)</option>
          <option value="revise_offer">Send revised offer with updated terms</option>
        </select>
        <label>Resolution</label>
      </div>
      {showRevisedTerms && (
        <OfferDetailsForm value={offerDetails} onChange={setOfferDetails} compact />
      )}
      <button
        type="button"
        className="border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-3 py-1.5 text-sm disabled:opacity-60"
        disabled={!canSend || mutation.isLoading}
        onClick={() =>
          mutation.mutate({
            message: message.trim(),
            resolution,
            offerDetails: showRevisedTerms ? offerDetails : undefined
          })
        }
      >
        {mutation.isLoading ? 'Sending…' : 'Send reply'}
      </button>
    </div>
  );
}
